import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiRequest } from "@/api/client";
import { apiErrorMessage } from "@/lib/apiError";

const SCOPES = ["cost-items:read", "cost-items:write"] as const;
type Scope = (typeof SCOPES)[number];

const apiTokenSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  token_hint: z.string(),
  scopes: z.array(z.string()),
  expires_at: z.string().nullable(),
  last_used_at: z.string().nullable(),
  created_at: z.string(),
});
type ApiToken = z.infer<typeof apiTokenSchema>;
const createdSchema = apiTokenSchema.extend({ token: z.string() });

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString("de-CH") : "—";

/**
 * "API-Zugänge" on the object detail page (OWNER only).
 *
 * The list endpoint is OWNER-only, so a 403/404 simply hides the section —
 * no separate role lookup. The plaintext token is shown exactly once after
 * creation; the backend never returns it again.
 */
export function ApiTokensSection({
  objectId,
}: {
  objectId: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ["api-tokens", objectId];
  const base = `/objects/${objectId}/api-tokens`;

  const list = useQuery({
    queryKey: key,
    queryFn: async () =>
      z.array(apiTokenSchema).parse(await apiRequest<unknown>(base)),
    retry: false,
  });

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("Ferienhaus-Manager");
  const [scopes, setScopes] = useState<Scope[]>([...SCOPES]);
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: async () =>
      createdSchema.parse(
        await apiRequest<unknown>(base, {
          method: "POST",
          json: { name, scopes },
          withCsrf: true,
        }),
      ),
    onSuccess: (created) => {
      setPlaintext(created.token);
      setCopied(false);
      setCreating(false);
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => setError(apiErrorMessage(e, t("common.error"))),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`${base}/${id}`, { method: "DELETE", withCsrf: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
    onError: (e) => setError(apiErrorMessage(e, t("common.error"))),
  });

  if (!list.isSuccess) return null;

  const toggleScope = (s: Scope) =>
    setScopes((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    createMut.mutate();
  };

  const copy = async () => {
    if (!plaintext) return;
    await navigator.clipboard.writeText(plaintext);
    setCopied(true);
  };

  const revoke = (tok: ApiToken) => {
    if (
      !window.confirm(t("objects.apiTokens.revokeConfirm", { name: tok.name }))
    )
      return;
    setError(null);
    revokeMut.mutate(tok.id);
  };

  return (
    <section
      className="mb-8 border-y border-rule py-4"
      aria-labelledby="api-tokens-title"
    >
      <div className="mb-2 flex items-center justify-between">
        <h3 id="api-tokens-title" className="text-lg font-medium">
          {t("objects.apiTokens.title")}
        </h3>
        {!creating && (
          <button
            type="button"
            onClick={() => {
              setCreating(true);
              setPlaintext(null);
            }}
            className="rounded border border-rule px-3 py-1 text-sm text-ink-muted hover:border-ink/30 hover:text-ink"
          >
            {t("objects.apiTokens.create")}
          </button>
        )}
      </div>
      <p className="mb-3 text-sm text-ink-muted">
        {t("objects.apiTokens.intro")}
      </p>

      {error && <p className="mb-3 text-sm text-negative">{error}</p>}

      {plaintext && (
        <div
          className="mb-4 rounded border border-rule bg-accent-soft p-3"
          role="status"
        >
          <p className="mb-2 text-sm font-medium text-ink">
            {t("objects.apiTokens.onceTitle")}
          </p>
          <p className="mb-2 text-sm text-ink-muted">
            {t("objects.apiTokens.onceHint")}
          </p>
          <code
            data-testid="api-token-plaintext"
            className="mb-2 block break-all rounded bg-paper-raised p-2 font-mono text-xs text-ink"
          >
            {plaintext}
          </code>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void copy()}
              className="rounded bg-ink px-3 py-1 text-sm text-paper hover:bg-ink/85"
            >
              {copied
                ? t("objects.apiTokens.copied")
                : t("objects.apiTokens.copy")}
            </button>
            <button
              type="button"
              onClick={() => setPlaintext(null)}
              className="rounded border border-rule px-3 py-1 text-sm text-ink-muted hover:border-ink/30 hover:text-ink"
            >
              {t("objects.apiTokens.done")}
            </button>
          </div>
        </div>
      )}

      {creating && (
        <form onSubmit={submit} className="mb-4 space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">
              {t("objects.apiTokens.name")}
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              required
              className="w-full rounded-sheet border border-rule bg-paper-raised px-2 py-1.5 text-ink focus:border-accent focus:outline-none"
            />
          </label>
          <fieldset className="text-sm">
            <legend className="mb-1 font-medium">
              {t("objects.apiTokens.scopes")}
            </legend>
            {SCOPES.map((s) => (
              <label key={s} className="mr-4 inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={scopes.includes(s)}
                  onChange={() => toggleScope(s)}
                />
                {t(
                  `objects.apiTokens.scope.${s === "cost-items:read" ? "read" : "write"}`,
                )}
              </label>
            ))}
          </fieldset>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={
                createMut.isPending || scopes.length === 0 || name.trim() === ""
              }
              className="rounded bg-ink px-3 py-1 text-sm text-paper hover:bg-ink/85 disabled:opacity-50"
            >
              {t("objects.apiTokens.submit")}
            </button>
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="rounded border border-rule px-3 py-1 text-sm text-ink-muted hover:border-ink/30 hover:text-ink"
            >
              {t("objects.apiTokens.cancel")}
            </button>
          </div>
        </form>
      )}

      {list.data.length === 0 ? (
        <p className="text-sm text-ink-muted">{t("objects.apiTokens.empty")}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-ink-muted">
            <tr className="border-b border-rule">
              <th className="px-2 py-2">{t("objects.apiTokens.name")}</th>
              <th className="px-2 py-2">{t("objects.apiTokens.hint")}</th>
              <th className="px-2 py-2">{t("objects.apiTokens.scopes")}</th>
              <th className="px-2 py-2">{t("objects.apiTokens.lastUsed")}</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {list.data.map((tok) => (
              <tr key={tok.id} className="border-b border-rule">
                <td className="px-2 py-2">{tok.name}</td>
                <td className="px-2 py-2 font-mono text-xs">
                  {tok.token_hint}…
                </td>
                <td className="px-2 py-2 font-mono text-xs">
                  {tok.scopes.join(", ")}
                </td>
                <td className="px-2 py-2 text-ink-muted">
                  {fmtDate(tok.last_used_at)}
                </td>
                <td className="px-2 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => revoke(tok)}
                    className="text-sm text-ink-muted hover:text-ink hover:underline"
                  >
                    {t("objects.apiTokens.revoke")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
